import { gql } from "@apollo/client";
import { Layout } from "components/layout";
import { useInputArray } from "lib/hooks/use-input-group";
import { IPostType, PostType, postTypes } from "lib/types";
import { isNumber, parseParam, range } from "lib/utils";
import {
  PostCardDataFragmentDoc,
  useGetAllArticlesQuery,
  GetAllArticlesDocument,
  GetAllArticlesQueryVariables,
  GetPageContentDocument,
  GetPageContentQueryVariables,
  useGetPageContentQuery,
} from "lib/graphql-generated";
import { GetServerSideProps } from "next";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import querystring from "querystring";
import { PostCard } from "components/post-card";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useInput } from "lib/hooks/use-input";
import {
  BooksellerSelect,
  BooksellerSelectorValue,
  DefaultBooksellerSelectorValue,
} from "components/forms/bookseller-select";
import { Button } from "components/buttons/button";
import { RenderContent } from "components/content-renderer";

const limit = 30;

const GET_ALL_ARTICLES = gql`
  query GetAllArticles($filters: posts_bool_exp, $offset: Int!, $limit: Int) {
    posts(
      where: $filters
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
    ) {
      ...PostCardData
    }

    posts_aggregate(where: $filters) {
      aggregate {
        count
      }
    }
  }
  ${PostCardDataFragmentDoc}
`;

interface Props {
  initialVariables: GetAllArticlesQueryVariables;
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();

  const query = ctx.query;

  let variables: Partial<GetAllArticlesQueryVariables> = {
    filters: {},
  };
  variables["limit"] = limit;

  if (query.p && query.p !== "") {
    const tempP = parseParam(query.p);
    const p: number = isNumber(tempP) ? parseInt(tempP) : 0;
    variables["offset"] = p * limit;
  } else {
    variables["offset"] = 0;
  }

  let filters: GetAllArticlesQueryVariables["filters"]["_and"] = [];

  if (query.bookseller && query.bookseller !== "") {
    const tempB = parseParam(query.bookseller);
    const decodedBS = JSON.parse(decodeURI(tempB));
    const bookseller = isNumber(decodedBS.id) ? parseInt(decodedBS.id) : null;
    if (bookseller) {
      filters.push({ author_id: { _eq: bookseller } });
    }
  }

  if (query.types && query.types !== "") {
    let types = Array.isArray(query.types) ? query.types : [query.types];
    filters.push({ type: { _in: types } });
  }

  if (filters.length > 0) {
    variables["filters"] = { _and: [] };
    variables["filters"]["_and"] = filters;
  }

  await client.query({
    query: GetAllArticlesDocument,
    variables: variables,
  });

  await client.query({
    query: GetPageContentDocument,
    variables: {
      title: "posts",
    } as GetPageContentQueryVariables,
  });

  return addApolloState(client, {
    props: {
      initialVariables: variables,
    } as Props,
  });
};

export const Articles = ({ initialVariables }: Props) => {
  const router = useRouter();
  const types = useInputArray(postTypes.map((type) => type.name));
  const [page, setPage] = useState<number>(
    initialVariables.offset / initialVariables.limit
  );
  const bookseller = useInput<BooksellerSelectorValue>(
    router.query.bookseller
      ? JSON.parse(decodeURI(parseParam(router.query.bookseller)))
      : DefaultBooksellerSelectorValue
  );

  let variables: GetAllArticlesQueryVariables = initialVariables;

  const { data } = useGetAllArticlesQuery({
    variables: variables,
  });

  const { data: pageContent } = useGetPageContentQuery({
    variables: {
      title: "posts",
    },
  });

  useEffect(() => {
    const path = router.pathname;
    const query = querystring.encode({
      p: page,
      bookseller: encodeURI(JSON.stringify(bookseller.value)),
      types: types.values,
    });

    router.replace(`${path}?${query}`, undefined, { scroll: false });
  }, [page, bookseller.value, types.values]);

  const getQueryString = (page: number): string => {
    const path = router.pathname;
    const query = querystring.encode({
      p: page,
      types: types.values,
      bookseller: encodeURI(JSON.stringify(bookseller.value)),
    });
    return `${path}?${query}`;
  };

  return (
    <>
      <Layout pageName='Posts' className=' max-w-screen-2xl mx-auto'>
        <article className='flex flex-col'>
          <header className='bg-green-light w-full flex justify-center py-8 px-8 2xl:px-0'>
            <h1 className='sr-only'>Articles, Reading Lists, Book Reviews</h1>
            <img
              src='/assets/images/article-header.png'
              className='max-h-96'
              alt='Articles, Reading Lists, Book Reviews'
            />
          </header>

          <section className='prose mx-auto pt-8'>
            <RenderContent
              content={pageContent.pages_by_pk.content.data[0].data}
            />
          </section>

          <section className='px-2 2xl:px-0 py-8 space-y-16 w-full max-w-screen-2xl mx-auto'>
            <nav className='place-items-center flex flex-col xl:flex-row space-y-8 xl:space-y-0 lg:justify-between'>
              <section className='flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-4'>
                <h2 className='uppercase font-header tracking-wide text-xl my-auto text-center'>
                  Filter Post Types:
                </h2>
                <ul className='flex flex-col md:flex-row space-y-4 md:space-x-10 md:space-y-0 mx-auto'>
                  {postTypes.map((type, index) => (
                    <li key={`posttypes_${index}`} className='my-auto'>
                      <label className='inline-flex items-center cursor-pointer'>
                        <input
                          type='checkbox'
                          className={`form-checkbox text-${type.color}`}
                          value={type.name}
                          checked={types.values.includes(type.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              types.addValue(e.target.value);
                            } else {
                              types.removeValue(e.target.value);
                            }
                          }}
                        />
                        <span className='ml-2 capitalize inline-flex'>
                          {type.name + "s"}
                          <svg className='h-4 w-6 ml-2 my-auto'>
                            <use href={`#${type.name}`} />
                          </svg>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </section>
              <section className='flex flex-col lg:flex-row'>
                <h2 className='uppercase font-header tracking-wide text-xl my-auto pb-2 lg:pb-0 text-center'>
                  Filter By Bookseller
                </h2>
                <BooksellerSelect
                  lateInitWithId={parseInt(parseParam(router.query.bookseller))}
                  label=''
                  setValue={bookseller.setValue}
                  value={bookseller.value}
                  className='-mt-2 lg:ml-2'
                />
                {bookseller.value.id !== -1 && (
                  <button
                    className='lg:ml-2 text-center mt-1 lg:mb-2 text-sm text-red-default hover:underline'
                    onClick={(e) => {
                      e.preventDefault();
                      bookseller.init(DefaultBooksellerSelectorValue);
                    }}>
                    Remove Filter
                  </button>
                )}
              </section>
            </nav>
            <ul className='space-y-16 md:px-4 md:grid md:grid-cols-3 md:gap-8 md:space-y-0 lg:grid-cols-5 mx-auto'>
              {data.posts.map((p) => (
                <PostCard post={p} key={p.id} />
              ))}
            </ul>
          </section>
          {data.posts && (
            <nav
              className='py-3 flex items-center justify-between border-t border-gray-200 max-w-screen-2xl '
              aria-label='Pagination'>
              <div className='hidden sm:block'>
                <p className='text-sm text-gray-700'>
                  Showing{" "}
                  <span className='font-medium'>
                    {initialVariables.offset}
                    {` `}
                  </span>
                  to{" "}
                  <span className='font-medium'>
                    {Math.min(
                      initialVariables.offset + limit,
                      data.posts_aggregate.aggregate.count
                    )}
                    {` `}
                  </span>
                  of{" "}
                  <span className='font-medium'>
                    {data.posts_aggregate.aggregate.count}
                    {` `}
                  </span>
                  results
                </p>
              </div>
              <div className='flex-1 flex justify-between space-x-2 sm:justify-end'>
                {initialVariables.offset !== 0 && (
                  <Button
                    buttonType='ghost'
                    buttonSize='lg'
                    href={getQueryString(initialVariables.offset / limit - 1)}>
                    previous
                  </Button>
                )}
                {initialVariables.offset / limit !==
                  Math.floor(data.posts_aggregate.aggregate.count / limit) && (
                  <Button
                    buttonType='ghost'
                    buttonSize='lg'
                    href={getQueryString(initialVariables.offset / limit + 1)}>
                    Next
                  </Button>
                )}
              </div>
            </nav>
          )}
        </article>
      </Layout>
    </>
  );
};

export default Articles;
